import { useForm } from '@magicdoorinc/form';
import { createMutation } from '@magicdoorinc/ui';
import { useSearchParams } from '@solidjs/router';
import { createSignal, onMount } from 'solid-js';
import { auth } from '~/config/auth';
import { createMagicDoorContext } from '~/contexts/utils';
import { authRepository } from '~/repositories/authRepository';
import type { CompanyOptionDto } from '~/swagger/portal/Api';

export enum ResetPasswordStep {
  InputAccount = 'InputAccount',
  InputPasswords = 'InputPasswords',
  LinkSended = 'LinkSended',
  ResetSuccess = 'ResetSuccess',
  SelectCompany = 'SelectCompany',
}

export const [ResetPasswordProvider, useResetPassword] = createMagicDoorContext('ResetPassword', () => {
  const [step, setStep] = createSignal<ResetPasswordStep>(ResetPasswordStep.InputAccount);

  const [companies, setCompanies] = createSignal<CompanyOptionDto[]>([]);

  const form = useForm();
  const [searchParams] = useSearchParams<{ token: string; 'request-id': string }>();

  const getResetPasswordEmail = createMutation(async () => {
    const { validateStatus } = form.validateForm();
    if (!validateStatus) return;
    const { companies } = await authRepository.getResetPasswordEmail({
      email: form.formStore.email,
      companyId: form.formStore.companyId,
    });

    if (companies?.length) {
      setStep(ResetPasswordStep.SelectCompany);
      setCompanies(companies);
      return;
    }

    setStep(ResetPasswordStep.LinkSended);
  });

  const resetPassword = createMutation(async () => {
    const { validateStatus } = form.validateForm();
    if (!validateStatus) return;
    await auth.resetPassword({
      token: searchParams.token,
      newPassword: form.formStore.password,
      requestId: searchParams['request-id'],
    });
    setStep(ResetPasswordStep.ResetSuccess);
  });

  onMount(() => {
    if (searchParams.token) {
      setStep(ResetPasswordStep.InputPasswords);
      form.setFormStore({
        token: searchParams.token,
      });
    }
  });

  const handleFormSubmit = () => {
    if ([ResetPasswordStep.InputAccount, ResetPasswordStep.SelectCompany].includes(step())) {
      getResetPasswordEmail();
    }
    if (step() === ResetPasswordStep.InputPasswords) {
      resetPassword();
    }
  };

  return {
    step,
    form,
    setStep,
    getResetPasswordEmail,
    resetPassword,
    handleFormSubmit,
    companies,
  };
});
